/**
 * Runtime Health (P29 + P30 strip).
 *
 * Live data from `/readyz` on both apps + proxy `/metrics` + dashboard
 * summary. The wireframe incident strip + decorative narrative were
 * removed — the page now shows only operational facts the gateway can
 * actually report.
 */
import { useMemo, type JSX } from 'react';
import { Activity, Database, ShieldCheck } from 'lucide-react';

import {
  parsePrometheusText,
  useCatalogServers,
  useControlPlaneReadyz,
  useDashboardSummary,
  useProxyMetrics,
  useProxyReadyz,
} from '@/api/index.js';
import { MetricCard } from '@/components/console/MetricCard.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { StatusPill } from '@/components/console/StatusPill.js';

export default function RuntimeHealth(): JSX.Element {
  const proxyReady = useProxyReadyz();
  const cpReady = useControlPlaneReadyz();
  const proxyMetrics = useProxyMetrics();
  const summary = useDashboardSummary();
  const servers = useCatalogServers();

  const parsed = useMemo(
    () => (proxyMetrics.data ? parsePrometheusText(proxyMetrics.data) : []),
    [proxyMetrics.data],
  );
  const totalRequests = parsed
    .filter((m) => m.name === 'http_requests_total')
    .reduce((s, m) => s + m.value, 0);
  const errorRequests = parsed
    .filter(
      (m) => m.name === 'http_requests_total' && m.labels.status && Number(m.labels.status) >= 500,
    )
    .reduce((s, m) => s + m.value, 0);

  const allOk =
    proxyReady.data?.status === 'ok' &&
    cpReady.data?.status === 'ok' &&
    !proxyReady.isError &&
    !cpReady.isError;

  return (
    <>
      <PageHeader
        eyebrow="Operate"
        title="Runtime Health"
        description="Live readiness probes from the proxy + control-plane and aggregate request totals from the proxy /metrics endpoint."
      >
        <StatusPill label={allOk ? 'Healthy' : 'Degraded'} tone={allOk ? 'good' : 'warn'} />
      </PageHeader>

      {(proxyReady.isError || cpReady.isError) && (
        <p className="subtle" style={{ fontSize: 14, marginBottom: 16 }}>
          One or both health endpoints unreachable. The proxy or control-plane systemd unit on the
          gateway box may be down — check <code>systemctl status sumcp-proxy</code> and{' '}
          <code>sumcp-control-plane</code>.
        </p>
      )}

      <div className="metric-grid">
        <MetricCard
          icon={ShieldCheck}
          label="Proxy"
          value={proxyReady.data?.status ?? '—'}
          delta={`auth: ${proxyReady.data?.checks.auth ?? '—'}`}
          tone={proxyReady.data?.status === 'ok' ? 'good' : 'warn'}
        />
        <MetricCard
          icon={ShieldCheck}
          label="Control plane"
          value={cpReady.data?.status ?? '—'}
          delta={`db: ${cpReady.data?.checks.db ?? '—'}`}
          tone={cpReady.data?.status === 'ok' ? 'good' : 'warn'}
        />
        <MetricCard
          icon={Activity}
          label="Proxy requests"
          value={totalRequests > 0 ? totalRequests.toLocaleString() : '0'}
          delta={`5xx: ${errorRequests}`}
          tone={errorRequests > 0 ? 'warn' : 'good'}
        />
        <MetricCard
          icon={Database}
          label="Audit (24h)"
          value={
            summary.data
              ? (
                  summary.data.audit_24h.allow +
                  summary.data.audit_24h.deny +
                  summary.data.audit_24h.error
                ).toLocaleString()
              : '—'
          }
          delta={`${summary.data?.audit_total_all_time ?? 0} all-time`}
          tone="good"
        />
      </div>

      <p className="subtle" style={{ fontSize: 14, marginBottom: 16 }}>
        Refreshes every 5–10s.
      </p>

      <SectionCard
        icon={ShieldCheck}
        title="Dependency checks"
        text="Per-component readiness state from each service's /readyz output."
      >
        <table className="data-table" style={{ fontSize: 15 }}>
          <thead>
            <tr>
              <th>Service</th>
              <th>Status</th>
              <th>Auth</th>
              <th>DB</th>
              <th>Registry</th>
              <th>Redis</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Proxy</strong>
              </td>
              <td>{proxyReady.data?.status ?? '—'}</td>
              <td>{proxyReady.data?.checks.auth ?? '—'}</td>
              <td>{proxyReady.data?.checks.db ?? '—'}</td>
              <td>—</td>
              <td>{proxyReady.data?.checks.redis ?? '—'}</td>
            </tr>
            <tr>
              <td>
                <strong>Control plane</strong>
              </td>
              <td>{cpReady.data?.status ?? '—'}</td>
              <td>{cpReady.data?.checks.auth ?? '—'}</td>
              <td>{cpReady.data?.checks.db ?? '—'}</td>
              <td>{cpReady.data?.checks.registry ?? '—'}</td>
              <td>{cpReady.data?.checks.redis ?? '—'}</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>

      <SectionCard
        icon={Activity}
        title={`Registered backends (${servers.data?.items.length ?? 0})`}
        text="Backends the proxy can route to."
      >
        {(servers.data?.items.length ?? 0) === 0 ? (
          <p className="subtle">No backends registered.</p>
        ) : (
          <table className="data-table" style={{ fontSize: 15 }}>
            <thead>
              <tr>
                <th>Slug</th>
                <th>Backend</th>
                <th>Status</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {(servers.data?.items ?? []).map((s) => (
                <tr key={s.id}>
                  <td>
                    <code>{s.slug}</code>
                  </td>
                  <td>
                    <code>{s.backendUrl}</code>
                  </td>
                  <td>{s.status}</td>
                  <td>{s.riskTier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>
    </>
  );
}
